import { DirectoryMapNode, DirectoryNode, RawFileItem, TabularFileItem } from 'types/CommonFileManager/commonFileManager';

export const formatSize = (bytes: number | string | undefined | null): string => {
    if (bytes === undefined || bytes === null || bytes === '') return '0 Bytes';
    if (typeof bytes === 'string') {
        const trimmed = bytes.trim();
        // If it already has size units like "1.5 MB", "500 KB", "10 Bytes", return as is
        if (/[a-zA-Z]/.test(trimmed)) {
            return trimmed;
        }
    }
    const numBytes = typeof bytes === 'string' ? parseFloat(bytes) : Number(bytes);
    if (isNaN(numBytes) || numBytes <= 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(numBytes) / Math.log(k));
    return parseFloat((numBytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

export const parseSizeStringToBytes = (sizeStr?: string | number | null): number => {
    if (!sizeStr) return 0;
    if (typeof sizeStr === 'number') return sizeStr;
    const trimmed = sizeStr.trim();
    if (!trimmed || trimmed === '0 Bytes') return 0;

    const match = trimmed.match(/^([\d.]+)\s*(Bytes|B|KB|MB|GB|TB)?$/i);
    if (!match) {
        const parsed = parseFloat(trimmed);
        return isNaN(parsed) ? 0 : parsed;
    }
    const val = parseFloat(match[1]);
    const unit = (match[2] || 'Bytes').toUpperCase();
    if (unit === 'KB') return val * 1024;
    if (unit === 'MB') return val * 1024 * 1024;
    if (unit === 'GB') return val * 1024 * 1024 * 1024;
    if (unit === 'TB') return val * 1024 * 1024 * 1024 * 1024;
    return val;
};

export const getDirectorySize = (dirId: string, apiFiles: RawFileItem[]): number => {
    const prefix = dirId === 'files' ? '' : dirId.replace(/^files\//, '') + '/';
    let totalBytes = 0;

    for (const item of (apiFiles || [])) {
        const parts = item.name.split('/');
        const fileName = parts[parts.length - 1];
        if (fileName === '.dir') continue;

        if (prefix === '' || item.name.startsWith(prefix)) {
            const rawSize = item.size ?? (item as any).fileSize ?? (item as any).Size ?? (item as any).FileSize ?? (item as any).length ?? (item as any).contentLength ?? 0;
            const numBytes = typeof rawSize === 'string' ? parseSizeStringToBytes(rawSize) : Number(rawSize || 0);
            totalBytes += (isNaN(numBytes) ? 0 : numBytes);
        }
    }

    return totalBytes;
};

export const parseApiData = (apiFiles: RawFileItem[]) => {
    const dirMap: Record<string, DirectoryMapNode> = {
        'files': { name: 'Files', parentId: null }
    };
    const flMap: Record<string, TabularFileItem[]> = {
        'files': []
    };

    const getFriendlyDate = (dateString?: string) => {
        if (!dateString) return '';
        try {
            return new Date(dateString).toLocaleString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: 'numeric',
                minute: 'numeric',
                hour12: true
            });
        } catch {
            return dateString;
        }
    };

    apiFiles.forEach((item, index) => {
        const fullPath = item.name;
        if (!fullPath) return;

        const parts = fullPath.split('/');
        const isFolderOnly = fullPath.endsWith('/');

        let fileName = '';
        let folderParts = parts;
        if (!isFolderOnly) {
            fileName = parts[parts.length - 1];
            folderParts = parts.slice(0, parts.length - 1);
        } else {
            folderParts = parts.filter(Boolean);
        }

        let currentParentId = 'files';
        const resolvedUserType = item.userType;
        folderParts.forEach((folderName, fIdx) => {
            const folderId = currentParentId === 'files' ? `files/${folderName}` : `${currentParentId}/${folderName}`;

            if (!dirMap[folderId]) {
                dirMap[folderId] = {
                    name: folderName,
                    parentId: currentParentId
                };
            }

            if (isFolderOnly && fIdx === folderParts.length - 1) {
                dirMap[folderId].modifiedDate = getFriendlyDate(item.modifiedOn || (item as any).modifiedDate || (item as any).createdOn || (item as any).createdDate);
                dirMap[folderId].modifiedBy = item.uploadedByUserName || (item as any).uploadedBy || (item as any).createdByUserName || '';
                dirMap[folderId].userType = resolvedUserType;
            }

            if (!flMap[folderId]) {
                flMap[folderId] = [];
            }

            currentParentId = folderId;
        });

        if (fileName === '.dir') {
            if (dirMap[currentParentId]) {
                dirMap[currentParentId].modifiedDate = getFriendlyDate(item.modifiedOn || (item as any).modifiedDate || (item as any).createdOn || (item as any).createdDate);
                dirMap[currentParentId].modifiedBy = item.uploadedByUserName || (item as any).uploadedBy || (item as any).createdByUserName || '';
                dirMap[currentParentId].userType = resolvedUserType;
            }
        } else if (!isFolderOnly && fileName) {
            if (!flMap[currentParentId]) {
                flMap[currentParentId] = [];
            }

            const rawSize = item.size ?? (item as any).fileSize ?? (item as any).Size ?? (item as any).FileSize ?? (item as any).length ?? (item as any).contentLength ?? 0;

            flMap[currentParentId].push({
                id: `file-${index}-${item.name}`,
                name: fileName,
                type: 'file',
                modifiedDate: getFriendlyDate(item.modifiedOn || (item as any).modifiedDate || (item as any).createdOn || (item as any).createdDate),
                size: formatSize(rawSize),
                modifiedBy: item.uploadedByUserName || (item as any).uploadedBy || (item as any).createdByUserName,
                note: item.notes || (item as any).note || '',
                userType: resolvedUserType
            });
        }
    });


    const rawByFolder: Record<string, RawFileItem[]> = {};
    apiFiles.forEach((item) => {
        if (!item.name || item.name.endsWith('/')) return;
        const parts = item.name.split('/');
        const fileName = parts[parts.length - 1];
        if (fileName === '.dir') return;
        const folderParts = parts.slice(0, parts.length - 1);
        let ancestorId = 'files';
        folderParts.forEach((f) => {
            ancestorId = ancestorId === 'files' ? `files/${f}` : `${ancestorId}/${f}`;
            if (!rawByFolder[ancestorId]) rawByFolder[ancestorId] = [];
            rawByFolder[ancestorId].push(item);
        });
    });

    Object.keys(dirMap).forEach((folderId) => {
        if (folderId === 'files') return;
        if (!dirMap[folderId].modifiedDate && !dirMap[folderId].modifiedBy) {
            const folderFiles = rawByFolder[folderId] || [];
            if (folderFiles.length > 0) {
                const latest = folderFiles.reduce((prev, curr) =>
                    (curr.modifiedOn || '') > (prev.modifiedOn || '') ? curr : prev
                );
                dirMap[folderId].modifiedDate = getFriendlyDate(latest.modifiedOn);
                dirMap[folderId].modifiedBy = latest.uploadedByUserName || '';
            }
        }
        if (dirMap[folderId].userType == null) {
            const folderFiles = rawByFolder[folderId] || [];
            if (folderFiles.length > 0) {
                const latest = folderFiles.reduce((prev, curr) =>
                    (curr.modifiedOn || '') > (prev.modifiedOn || '') ? curr : prev
                );
                dirMap[folderId].userType = latest.userType;
            }
        }
    });

    return { dirMap, flMap };
};

export const DEFAULT_DIRECTORY_MAP: Record<string, DirectoryMapNode> = {
    'files': { name: 'Files', parentId: null }
};

export const DEFAULT_FILES_MAP: Record<string, TabularFileItem[]> = {
    'files': []
};

export const getUniqueFileName = (existingNames: Set<string>, name: string): string => {
    const lowerName = name.toLowerCase();
    if (!existingNames.has(lowerName)) return name;

    const dotIndex = name.lastIndexOf('.');
    const baseName = dotIndex === -1 ? name : name.substring(0, dotIndex);
    const extension = dotIndex === -1 ? '' : name.substring(dotIndex);

    let counter = 1;
    let uniqueName = `${baseName} (${counter})${extension}`;
    while (existingNames.has(uniqueName.toLowerCase())) {
        counter++;
        uniqueName = `${baseName} (${counter})${extension}`;
    }
    return uniqueName;
};

export const getFolderLevel = (map: Record<string, DirectoryMapNode>, id: string): number => {
    let level = 0;
    let currentId = map[id]?.parentId;
    while (currentId) {
        level++;
        currentId = map[currentId]?.parentId;
    }
    return level;
};

export const getRecursiveStats = (
    dirId: string,
    dirMap: Record<string, DirectoryMapNode>,
    filesMap: Record<string, TabularFileItem[]>
): { filesCount: number; foldersCount: number } => {
    let filesCount = 0;
    let foldersCount = 0;

    // Files in the current directory
    filesCount += (filesMap[dirId] || []).length;

    // Direct child folders
    const childFolderIds = Object.keys(dirMap).filter((key) => dirMap[key].parentId === dirId);
    foldersCount += childFolderIds.length;

    // Recursively count in children
    for (const childId of childFolderIds) {
        const stats = getRecursiveStats(childId, dirMap, filesMap);
        filesCount += stats.filesCount;
        foldersCount += stats.foldersCount;
    }

    return { filesCount, foldersCount };
};

export const getPathForDirectory = (map: Record<string, DirectoryMapNode>, directoryId: string): string[] => {
    const path: string[] = [];
    let currentId: string | null = directoryId;
    while (currentId && map[currentId]) {
        path.unshift(map[currentId].name);
        currentId = map[currentId].parentId;
    }
    return path.length > 0 ? path : ['Files'];
};

export const getIdChainForDirectory = (map: Record<string, DirectoryMapNode>, directoryId: string): string[] => {
    const chain: string[] = [];
    let currentId: string | null = directoryId;
    while (currentId && map[currentId]) {
        chain.unshift(currentId);
        currentId = map[currentId].parentId;
    }
    return chain;
};

export const buildTree = (map: Record<string, DirectoryMapNode>, rootId: string = 'files'): DirectoryNode => {
    const node: DirectoryNode = {
        id: rootId,
        name: map[rootId]?.name || 'Files',
        children: []
    };

    const childIds = Object.keys(map).filter((key) => map[key].parentId === rootId);
    for (const childId of childIds) {
        node.children!.push(buildTree(map, childId));
    }

    if (node.children!.length === 0) {
        delete node.children;
    }

    return node;
};

export const getItemsForDirectory = (
    dirId: string,
    currentMap: Record<string, DirectoryMapNode>,
    currentFiles: Record<string, TabularFileItem[]>,
    apiFiles: RawFileItem[]
): TabularFileItem[] => {
    // 1. Get all child directories
    const childDirs = Object.entries(currentMap)
        .filter(([_, node]) => node.parentId === dirId)
        .map(([id, node]) => {
            const bytes = getDirectorySize(id, apiFiles);
            return {
                id,
                name: node.name,
                type: 'directory' as const,
                modifiedDate: node.modifiedDate || '',
                size: formatSize(bytes),
                modifiedBy: node.modifiedBy || '',
                userType: node.userType
            };
        });

    // 2. Get files for this directory
    const childFiles = currentFiles[dirId] || [];

    return [...childDirs, ...childFiles];
};
